import type React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ActionOutcome, ActionSurface } from '../../controlCenterSurfaces';

type ActionSurfaceHostProps = {
    /** The open action's registered surface, or `null` for anything else. */
    surface: ActionSurface | null;
    /** Changes on every open, so the surface remounts. */
    instanceId: number;
    onClose: () => void;
    onDone: (outcome: ActionOutcome) => void;
};

/**
 * Mounts the feature's own UI for whichever action is open.
 *
 * Both rules that make a surface safe to reuse live here, once, rather than in
 * each adapter — an adapter author will forget them and this file cannot:
 *
 * - **Mounted only while open.** Every surface wraps something that fetches;
 *   the employee drawer reads the whole register on mount for its duplicate-id
 *   check. A surface rendered permanently and merely hidden would be a request
 *   on every visit to the home page.
 * - **A fresh mount per opening.** `instanceId` changes on every press, so the
 *   `key` changes and the surface starts blank. Overlays in this app seed their
 *   fields from props once, at mount; without this the second opening shows the
 *   first one's draft. The salaries register had to learn this and the rule
 *   travels with the component.
 *
 * The boundary is here for a different reason than most: from the launcher's
 * point of view a surface is **foreign code** — it belongs to another page and
 * fetches things this page knows nothing about. It carries no `onRetry`,
 * because closing and pressing again is the retry, and a Retry button with
 * nothing to refetch would be a button that does nothing.
 */
export const ActionSurfaceHost: React.FC<ActionSurfaceHostProps> = ({
    surface,
    instanceId,
    onClose,
    onDone,
}) => {
    const { t } = useTranslation('pages');

    if (!surface) {
        return null;
    }

    const { Surface } = surface;

    return (
        <ErrorBoundary
            data-testid="control-center-surface-error-boundary"
            fallbackMessage={t('controlCenter.surface.errorMessage')}
            id="control-center-surface-error-boundary"
            title={t('controlCenter.surface.errorTitle')}
        >
            <Surface
                key={`control-center-surface-${instanceId}`}
                onClose={onClose}
                onDone={onDone}
            />
        </ErrorBoundary>
    );
};
